import { Blackboard, tagValue, untagValue } from "./Blackboard";
import { callFunction, type FunctionDefinition } from "./callFunction";
import { EventBus } from "./EventBus";
import { evaluate } from "./ExprEval";
import { InventoryManager } from "./InventoryManager";
import { Ternary } from "./Ternary";
import { TriggerEngine } from "./TriggerEngine";
import { RuleEngine, type RuleDefinition } from "./RuleEngine";
import type { GameClock } from "./GameClock";
import { resolveBackground } from "./transitionRoom";
import { resolveObjectSprite, isObjectHidden } from "./objectSprites";
import type { Shelf } from "./ObjectPlacement";
import type { Entities, TriggerDefinition, RecipeDefinition, WireVerbEntry, WireExitEntry } from "./Interpreter";

/** Fallback render scale for shelved (ambient) object sprites when neither the
 * object nor its place sets `shelf_scale`. Smaller than the featured-presentation
 * size so ambient objects read as realistically-sized props (spec §9). */
const DEFAULT_SHELF_SCALE = 0.3;

export interface ExitInfo {
  target: string;
  dir?: string;
  verbs: string[];
  available: boolean;
  /** Minutes charged on traversal (v2); undefined falls back to the global exit_cost. */
  timeCost?: number;
}

export interface RoomState {
  id: string;
  name: string;
  desc: string[];
  background: string;
  exits: ExitInfo[];
  characters: string[];
  objects: string[];
  roomVerbs: VerbEntry[];
}

export interface InventoryItem {
  id: string;
  name: string;
  icon: string;
  weight: number;
  verbs: string[];
}

export interface VerbEntry {
  name: string;
  available: boolean;
  lockReason?: string;
}

export type DispatchResult =
  | { type: "narrate"; text: string }
  | { type: "trigger"; scene: string }
  | { type: "move"; from: string; to: string }
  | { type: "fail"; text: string };

const DEFAULT_WEIGHT = 1;

export class SandboxEngine {
  private _entities: Entities;
  private _bb: Blackboard;
  private _bus: EventBus;
  private _inventory: InventoryManager;
  private _triggerEngine: TriggerEngine = new TriggerEngine();
  private _ruleEngine: RuleEngine | null = null;
  private _gameClock: GameClock | null = null;
  private _exitSandbox: ((scene: string) => void) | null = null;
  private _recipes: RecipeDefinition[] = [];
  private _functions: FunctionDefinition[] = [];
  private _assembleMode = false;
  private _assembleFirst: string | null = null;
  private _giveMode = false;
  private _giveItem: string | null = null;
  /** Game-wide default shelf scale (meta.display.shelf_scale), under per-place/per-object. */
  private _defaultShelfScale: number = DEFAULT_SHELF_SCALE;

  constructor(entities: Entities, bb: Blackboard, bus: EventBus, defaultShelfScale?: number) {
    this._entities = entities;
    this._bb = bb;
    this._bus = bus;
    this._inventory = new InventoryManager(bb, entities.objects);
    this._inventory.initPlacement(entities.places);
    if (typeof defaultShelfScale === "number" && defaultShelfScale > 0) {
      this._defaultShelfScale = defaultShelfScale;
    }
  }

  loadRules(rules: RuleDefinition[]): void {
    if (rules.length === 0) return;
    const engine = new RuleEngine();
    engine.register(rules, this._bb);
    this._ruleEngine = engine;
  }

  loadTriggers(triggers: TriggerDefinition[]): void {
    this._triggerEngine.load(triggers);
  }

  loadRecipes(recipes: RecipeDefinition[]): void {
    this._recipes = recipes;
  }

  loadFunctions(fns: Array<{ name: string; args: string[]; dest: string; body_ast: unknown }>): void {
    this._functions = fns as FunctionDefinition[];
  }

  setExitCallback(cb: (scene: string) => void): void {
    this._exitSandbox = cb;
  }

  /** Wire the shared game clock so sandbox actions can charge in-world time (v2). */
  setGameClock(clock: GameClock | null): void {
    this._gameClock = clock;
  }

  /** Advance the clock by a positive cost; no-op when time is disabled or cost <= 0. */
  private _advanceTime(minutes: number): void {
    if (this._gameClock && minutes > 0) this._gameClock.advance(minutes);
  }

  /** HUD "wait" action — let the player spend `minutes` of in-world time. */
  wait(minutes: number): void {
    this._advanceTime(minutes);
    this._tickRules();
  }

  /** HUD "wait until" action — advance to the next occurrence of `h:m`. */
  waitUntil(hour: number, minute: number): void {
    if (!this._gameClock) return;
    this._advanceTime(this._gameClock.minutesUntil(hour, minute));
    this._tickRules();
  }

  /**
   * Minutes declared on the (entity, verb) pair via a `time_cost:` annotation,
   * searching room / character / object verb lists. 0 when none — so inspection
   * verbs are free unless the writer explicitly prices them.
   */
  private _verbTimeCost(entityId: string, verb: string): number {
    const lists: WireVerbEntry[][] = [];
    const place = this._entities.places[this.getCurrentPlace()];
    if (place?.verbs) lists.push(place.verbs);
    const char = this._entities.characters[entityId];
    if (char?.verbs) lists.push(char.verbs);
    const obj = this._entities.objects[entityId];
    if (obj?.verbs) lists.push(obj.verbs);
    for (const list of lists) {
      for (const entry of list) {
        if (typeof entry !== "string" && entry.name === verb && typeof entry.time_cost === "number") {
          return entry.time_cost;
        }
      }
    }
    return 0;
  }

  /** Exit traversal cost: the exit's own time_cost, else the global exit_cost default. */
  private _exitTimeCost(exit: ExitInfo): number {
    return exit.timeCost ?? this._gameClock?.cfg.exit_cost ?? 0;
  }

  getCurrentPlace(): string {
    if (this._bb.has("_current_place")) {
      return String(untagValue(this._bb.get("_current_place")));
    }
    return "";
  }

  getRoom(placeId?: string): RoomState | null {
    const id = placeId ?? this.getCurrentPlace();
    const place = this._entities.places[id];
    if (!place) return null;

    return {
      id,
      name: place.name ?? id,
      desc: place.desc ?? [],
      background: resolveBackground(place.background, place.image, undefined) ?? "",
      exits: this._normalizeExits(place.exits ?? []),
      characters: place.characters ?? [],
      objects: this._inventory.getObjectsInRoom(id),
      roomVerbs: this._resolveVerbEntries(place.verbs ?? ["look"]),
    };
  }

  private _normalizeExits(wireExits: WireExitEntry[]): ExitInfo[] {
    return wireExits.map((e) => {
      if (typeof e === "string") {
        return { target: e, verbs: ["go"], available: true };
      }
      let available = true;
      if (e.cond_ast) {
        const condResult = evaluate(e.cond_ast, this._bb);
        const ternary = condResult instanceof Ternary ? condResult : Ternary.from(condResult);
        available = ternary.isTrue;
      }
      const info: ExitInfo = { target: e.target, verbs: e.verbs ?? ["go"], available };
      if (e.dir) info.dir = e.dir;
      if (typeof e.time_cost === "number") info.timeCost = e.time_cost;
      return info;
    });
  }

  getCurrentRoom(): RoomState | null {
    return this.getRoom();
  }

  /**
   * Emit the current room's placeable objects + shelves so the renderer can lay
   * them out (P5). Skips inventoried objects (not in the room), `hidden_when`
   * objects, and spriteless objects. Deterministic placement happens renderer-side.
   *
   * Each item carries a `scale`: shelved (ambient) sprites are typically smaller
   * and more realistically sized than their featured-presentation size. Resolution
   * is per-object `shelf_scale` → place `shelf_scale` → engine default, clamped > 0.
   */
  emitRoomObjects(): void {
    const placeId = this.getCurrentPlace();
    const place = this._entities.places[placeId];
    if (!place) return;
    const placeScale = typeof place.shelf_scale === "number" && place.shelf_scale > 0 ? place.shelf_scale : undefined;
    const items: Array<{ id: string; asset: string; scale: number }> = [];
    for (const id of this._inventory.getObjectsInRoom(placeId)) {
      const obj = this._entities.objects[id];
      if (isObjectHidden(obj, this._bb)) continue;
      const asset = resolveObjectSprite(obj, this._bb);
      if (!asset) continue;
      const objScale = typeof obj?.shelf_scale === "number" && obj.shelf_scale > 0 ? obj.shelf_scale : undefined;
      const scale = objScale ?? placeScale ?? this._defaultShelfScale;
      items.push({ id, asset, scale });
    }
    const shelves = (place.shelves as Shelf[] | undefined) ?? [];
    this._bus.emit("object.room", { items, shelves });
  }

  getExitName(exitId: string): string {
    const place = this._entities.places[exitId];
    if (place) return place.name ?? exitId;
    return exitId;
  }

  getRoomVerbEntries(): VerbEntry[] {
    const room = this.getCurrentRoom();
    if (!room) return [];
    return room.roomVerbs;
  }

  getEntityName(entityId: string): string {
    const char = this._entities.characters[entityId];
    if (char) return char.display_name ?? char.name ?? entityId;
    const obj = this._entities.objects[entityId];
    if (obj) return obj.name ?? entityId;
    const place = this._entities.places[entityId];
    if (place) return place.name ?? entityId;
    return entityId;
  }

  getEntityVerbs(entityId: string): string[] {
    return this.getEntityVerbEntries(entityId)
      .filter((e) => e.available)
      .map((e) => e.name);
  }

  getEntityVerbEntries(entityId: string): VerbEntry[] {
    const char = this._entities.characters[entityId];
    if (char) {
      const raw: WireVerbEntry[] = char.verbs ?? ["talk", "examine"];
      return this._resolveVerbEntries(raw);
    }
    const obj = this._entities.objects[entityId];
    if (obj) {
      const raw: WireVerbEntry[] = obj.verbs ?? ["examine"];
      const entries = this._resolveVerbEntries(raw);
      if (this._inventory.has(entityId)) {
        const filtered = entries.filter((e) => e.name !== "take");
        if (!filtered.some((e) => e.name === "drop")) {
          filtered.unshift({ name: "drop", available: true });
        }
        if (this._recipes.length > 0 && !filtered.some((e) => e.name === "assemble")) {
          filtered.push({ name: "assemble", available: true });
        }
        return filtered;
      }
      return entries;
    }
    return [];
  }

  private _resolveVerbEntries(raw: WireVerbEntry[]): VerbEntry[] {
    const result: VerbEntry[] = [];
    for (const entry of raw) {
      if (typeof entry === "string") {
        result.push({ name: entry, available: true });
      } else {
        if (!entry.cond_ast) {
          result.push({ name: entry.name, available: true });
          continue;
        }
        const condResult = evaluate(entry.cond_ast, this._bb);
        const ternary = condResult instanceof Ternary ? condResult : Ternary.from(condResult);
        if (!ternary.isTrue && entry.hidden_when_false) continue;
        const verbEntry: VerbEntry = { name: entry.name, available: ternary.isTrue };
        if (!ternary.isTrue && entry.cond) verbEntry.lockReason = String(entry.cond);
        result.push(verbEntry);
      }
    }
    return result;
  }

  // --- Inventory ---

  getInventory(): InventoryItem[] {
    return this._inventory.getIds().map((id) => {
      const obj = this._entities.objects[id] ?? {};
      return {
        id,
        name: obj.name ?? id,
        icon: obj.icon ?? "",
        weight: obj.weight ?? DEFAULT_WEIGHT,
        verbs: this.getEntityVerbs(id),
      };
    });
  }

  getCapacity(): { used: number; max: number } {
    return this._inventory.getCapacity();
  }

  // --- Two-step verb modes ---

  get assembleMode(): boolean { return this._assembleMode; }
  get assembleFirst(): string | null { return this._assembleFirst; }
  get giveMode(): boolean { return this._giveMode; }
  get giveItem(): string | null { return this._giveItem; }

  startAssemble(itemId: string): DispatchResult {
    this._assembleMode = true;
    this._assembleFirst = itemId;
    return { type: "narrate", text: "Select an item to combine with..." };
  }

  cancelAssemble(): void {
    this._assembleMode = false;
    this._assembleFirst = null;
  }

  completeAssemble(secondItemId: string): DispatchResult {
    const first = this._assembleFirst;
    this._assembleMode = false;
    this._assembleFirst = null;
    if (!first) return { type: "fail", text: "Nothing to combine." };
    return this.assemble(first, secondItemId);
  }

  startGive(itemId: string): DispatchResult {
    this._giveMode = true;
    this._giveItem = itemId;
    return { type: "narrate", text: "Give to whom?" };
  }

  cancelGive(): void {
    this._giveMode = false;
    this._giveItem = null;
  }

  completeGive(targetCharId: string): DispatchResult {
    const item = this._giveItem;
    this._giveMode = false;
    this._giveItem = null;
    if (!item) return { type: "fail", text: "Nothing to give." };
    return this.give(item, targetCharId);
  }

  // --- Assemble ---

  assemble(itemA: string, itemB: string): DispatchResult {
    const recipe = this._recipes.find(
      (r) => (r.a === itemA && r.b === itemB) || (r.a === itemB && r.b === itemA),
    );
    if (!recipe) {
      this._bus.emit("sandbox.narrate", { text: "These items can't be combined." });
      return { type: "narrate", text: "These items can't be combined." };
    }

    if (!this._inventory.has(itemA) || !this._inventory.has(itemB)) {
      return { type: "fail", text: "You don't have both items." };
    }

    const resultObj = this._entities.objects[recipe.result];
    if (!resultObj) {
      return { type: "fail", text: "Something went wrong." };
    }

    const weight = resultObj.weight ?? DEFAULT_WEIGHT;
    const weightA = this._inventory.getItemWeight(itemA);
    const weightB = this._inventory.getItemWeight(itemB);
    const cap = this.getCapacity();
    if (cap.used - weightA - weightB + weight > cap.max) {
      this._bus.emit("sandbox.narrate", { text: "The result would be too heavy to carry." });
      return { type: "narrate", text: "The result would be too heavy to carry." };
    }

    const ids = this._inventory.getIds().filter((id) => id !== itemA && id !== itemB);
    ids.push(recipe.result);
    this._inventory.setIds(ids);
    this._inventory.setPlace(recipe.result, "_inventory");
    this._inventory.setPlace(itemA, "_consumed");
    this._inventory.setPlace(itemB, "_consumed");

    const resultName = resultObj.name ?? recipe.result;
    const text = `You combine the items and create: ${resultName}.`;
    this._bus.emit("sandbox.narrate", { text });
    this._bus.emit("sandbox.inventory", { action: "assemble", objectId: recipe.result, inventory: ids });
    this._presentObject(recipe.result, text, "fade");
    return { type: "narrate", text };
  }

  give(itemId: string, targetCharId: string): DispatchResult {
    if (!this._inventory.has(itemId)) {
      return { type: "fail", text: "You don't have that." };
    }

    const char = this._entities.characters[targetCharId];
    if (!char) {
      return { type: "fail", text: "You can't give things to that." };
    }

    const room = this.getCurrentPlace();
    const roomChars = this._entities.places[room]?.characters ?? [];
    if (!roomChars.includes(targetCharId)) {
      return { type: "fail", text: `${this.getEntityName(targetCharId)} isn't here.` };
    }

    const itemName = this.getEntityName(itemId);
    const charName = this.getEntityName(targetCharId);

    const ids = this._inventory.remove(itemId);
    this._inventory.setPlace(itemId, `_held_${targetCharId}`);
    this._bus.emit("sandbox.inventory", { action: "drop", objectId: itemId, inventory: ids });

    const text = `You give the ${itemName} to ${charName}.`;
    this._bus.emit("sandbox.narrate", { text });
    this._tickRules();
    return { type: "narrate", text };
  }

  // --- Movement ---

  moveEntity(entityId: string, targetPlaceId: string, expectedFrom?: string): DispatchResult {
    const isChar = !!this._entities.characters[entityId];
    const isObj = !!this._entities.objects[entityId];
    if (!isChar && !isObj) {
      return { type: "fail", text: `Unknown entity: ${entityId}.` };
    }

    const isSpecial = targetPlaceId.startsWith("_");
    if (!isSpecial && !this._entities.places[targetPlaceId]) {
      return { type: "fail", text: `Unknown place: ${targetPlaceId}.` };
    }

    const placeKey = `_place_${entityId}`;
    const currentPlace = this._bb.has(placeKey) ? String(untagValue(this._bb.get(placeKey))) : null;

    if (currentPlace === "_consumed") {
      return { type: "fail", text: `${this.getEntityName(entityId)} no longer exists.` };
    }

    if (expectedFrom !== undefined && currentPlace !== expectedFrom) {
      return { type: "fail", text: `${this.getEntityName(entityId)} isn't there.` };
    }

    this._bb.set(placeKey, tagValue(targetPlaceId));
    this._tickRules();
    const name = this.getEntityName(entityId);
    const destName = isSpecial ? targetPlaceId : (this._entities.places[targetPlaceId]?.name ?? targetPlaceId);
    return { type: "narrate", text: `${name} moves to ${destName}.` };
  }

  move(targetPlaceId: string): boolean {
    const current = this.getCurrentRoom();
    if (!current) return false;

    const exit = current.exits.find((e) => e.target === targetPlaceId);
    if (!exit || !exit.available) return false;

    const from = current.id;
    this._bb.set("_current_place", tagValue(targetPlaceId));
    this._advanceTime(this._exitTimeCost(exit));
    this._bus.emit("sandbox.move", { from, to: targetPlaceId });
    this._tickRules();
    return true;
  }

  private _tickRules(): void {
    if (this._ruleEngine) {
      this._ruleEngine.tick(this._bb);
    }
  }

  // --- Verb Dispatch ---

  dispatch(entityId: string, verb: string): DispatchResult {
    // 1. Two-step mode intercept — any dispatch during a mode completes it
    if (this._assembleMode && this._assembleFirst) {
      return this.completeAssemble(entityId);
    }
    if (this._giveMode && this._giveItem) {
      return this.completeGive(entityId);
    }

    const room = this.getCurrentPlace();

    // 2. Check triggers
    const triggerMatch = this._triggerEngine.evaluate(entityId, verb, room, this._bb);
    if (triggerMatch) {
      this._bus.emit("sandbox.verb", { entity: entityId, verb });
      if (this._exitSandbox) {
        this._exitSandbox(triggerMatch.scene);
      }
      return { type: "trigger", scene: triggerMatch.scene };
    }

    // 3. Two-step verb entry
    if (verb === "assemble") {
      return this.startAssemble(entityId);
    }
    if (verb === "give") {
      return this.startGive(entityId);
    }

    // 4. Check verb function override (verb_{verb})
    //    Effect runs first (inside the func), then time advances: the func's
    //    declared time_cost plus any dynamic `_time_cost` it computed (§7.3).
    const overrideResult = this._tryVerbFunction(entityId, verb);
    if (overrideResult) {
      this._bus.emit("sandbox.verb", { entity: entityId, verb, result: overrideResult });
      this._bus.emit("sandbox.narrate", { text: overrideResult });
      this._advanceTime(this._verbTimeCost(entityId, verb));
      return { type: "narrate", text: overrideResult };
    }

    // 5. Fall back to built-in handler
    const result = this._builtinHandler(entityId, verb);
    const narrationText = result.type === "narrate" ? result.text : undefined;
    if (narrationText !== undefined) {
      this._bus.emit("sandbox.verb", { entity: entityId, verb, result: narrationText });
    } else {
      this._bus.emit("sandbox.verb", { entity: entityId, verb });
    }
    if (result.type === "narrate") {
      this._bus.emit("sandbox.narrate", { text: result.text });
    }

    // Effect-then-advance: charge the declared verb cost only on a successful
    // action (a "narrate" result). Failures ("fail") and movement ("move",
    // charged via the exit) don't pay here.
    if (result.type === "narrate") {
      this._advanceTime(this._verbTimeCost(entityId, verb));
    }

    // 6. Re-evaluate ~auto rules (state may have changed)
    this._tickRules();

    return result;
  }

  private _tryVerbFunction(entityId: string, verb: string): string | null {
    const fnName = `verb_${verb}`;
    const fnDef = this._functions.find((f) => f.name === fnName);
    if (!fnDef) return null;

    this._bb.set("_result", tagValue(""));
    this._bb.set("_time_cost", tagValue(0));
    callFunction(this._bb, fnDef, [entityId]);

    const resultVal = this._bb.has("_result") ? String(untagValue(this._bb.get("_result"))) : "";
    this._bb.set("_result", tagValue(""));

    // Surface #3 (§7.3): the func may have computed a dynamic time cost in
    // `_time_cost`. Read it (effect already happened), then advance.
    const rawCost = this._bb.has("_time_cost") ? Number(untagValue(this._bb.get("_time_cost"))) : 0;
    this._bb.set("_time_cost", tagValue(0));
    if (Number.isFinite(rawCost) && rawCost > 0) this._advanceTime(rawCost);

    return resultVal || null;
  }

  private _builtinHandler(entityId: string, verb: string): DispatchResult {
    switch (verb) {
      case "look":
        return this._handleLook();
      case "examine":
        return this._handleExamine(entityId);
      case "go":
        return this._handleGo(entityId);
      case "talk":
        return this._handleTalk(entityId);
      case "take":
        return this._handleTake(entityId);
      case "drop":
        return this._handleDrop(entityId);
      case "sleep":
        return this._handleSleep();
      default:
        return { type: "narrate", text: `You can't do that right now.` };
    }
  }

  /**
   * Built-in `sleep` verb (v4): advance the clock to the next wake time
   * (`calendar.wake_hour:wake_minute`, default = the day's start). Catch-up
   * fires any overnight-scheduled rules in order and `day()` rolls over. When
   * the player is already at the wake time, a full day passes.
   */
  private _handleSleep(): DispatchResult {
    const gc = this._gameClock;
    if (!gc) return { type: "narrate", text: "You rest for a while." };
    let minutes = gc.minutesUntil(gc.cfg.wake_hour, gc.cfg.wake_minute);
    if (minutes === 0) minutes = gc.cfg.minutes_per_hour * gc.cfg.hours_per_day;
    gc.advance(minutes);
    return { type: "narrate", text: `You sleep, waking at ${gc.timeStr()}.` };
  }

  private _handleLook(): DispatchResult {
    const room = this.getCurrentRoom();
    if (!room) return { type: "fail", text: "You can't see anything." };
    const desc = room.desc.length > 0 ? room.desc.join(" ") : `You are in ${room.name}.`;
    return { type: "narrate", text: desc };
  }

  /**
   * Fire the featured presentation for an object verb (take/drop/examine/craft),
   * if the object has a sprite. Spriteless objects (fixtures, abstract items)
   * just narrate. See the object-sprites design note §5.
   */
  private _presentObject(id: string, caption: string, exit: string): void {
    const asset = resolveObjectSprite(this._entities.objects[id], this._bb);
    if (asset) this._bus.emit("object.present", { id, asset, caption, exit });
  }

  private _handleExamine(entityId: string): DispatchResult {
    const obj = this._entities.objects[entityId];
    if (obj) {
      const text = obj.desc?.join(" ") || `You see nothing special about the ${this.getEntityName(entityId)}.`;
      this._presentObject(entityId, text, "return");
      return { type: "narrate", text };
    }
    const char = this._entities.characters[entityId];
    if (char) {
      const desc = char.desc?.join(" ") ?? "";
      return { type: "narrate", text: desc || `You see ${this.getEntityName(entityId)}.` };
    }
    return { type: "narrate", text: `You see nothing special.` };
  }

  private _handleGo(targetPlaceId: string): DispatchResult {
    const current = this.getCurrentRoom();
    if (!current) return { type: "fail", text: "You can't go anywhere." };

    const exit = current.exits.find((e) => e.target === targetPlaceId);
    if (!exit || !exit.available) {
      return { type: "fail", text: "You can't go that way." };
    }

    const from = current.id;
    this._bb.set("_current_place", tagValue(targetPlaceId));
    this._advanceTime(this._exitTimeCost(exit));
    this._bus.emit("sandbox.move", { from, to: targetPlaceId });
    return { type: "move", from, to: targetPlaceId };
  }

  private _handleTalk(entityId: string): DispatchResult {
    const name = this.getEntityName(entityId);
    return { type: "narrate", text: `${name} has nothing to say.` };
  }

  private _handleTake(entityId: string): DispatchResult {
    const obj = this._entities.objects[entityId];
    if (!obj) return { type: "fail", text: "You can't take that." };

    const name = this.getEntityName(entityId);

    if (!obj.inventory_possible) {
      return { type: "narrate", text: `You can't pick up the ${name}.` };
    }

    if (this._inventory.has(entityId)) {
      return { type: "narrate", text: `You already have the ${name}.` };
    }

    const room = this.getCurrentPlace();
    const placeKey = `_place_${entityId}`;
    if (this._bb.has(placeKey)) {
      const objRoom = String(untagValue(this._bb.get(placeKey)));
      if (objRoom !== room) {
        return { type: "fail", text: `The ${name} isn't here.` };
      }
    }

    const weight = obj.weight ?? DEFAULT_WEIGHT;
    const cap = this.getCapacity();
    if (cap.used + weight > cap.max) {
      return { type: "narrate", text: "You can't carry any more." };
    }

    this._inventory.add(entityId);
    const ids = this._inventory.getIds();
    this._bus.emit("sandbox.inventory", { action: "take", objectId: entityId, inventory: ids });
    this._presentObject(entityId, `You pick up the ${name}.`, "fade");
    return { type: "narrate", text: `You pick up the ${name}.` };
  }

  private _handleDrop(entityId: string): DispatchResult {
    if (!this._inventory.has(entityId)) {
      return { type: "fail", text: "You don't have that." };
    }

    const name = this.getEntityName(entityId);
    const room = this.getCurrentPlace();
    const ids = this._inventory.remove(entityId);
    this._inventory.setPlace(entityId, room);
    this._bus.emit("sandbox.inventory", { action: "drop", objectId: entityId, inventory: ids });
    this._presentObject(entityId, `You drop the ${name}.`, "return");
    return { type: "narrate", text: `You drop the ${name}.` };
  }
}
